import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Layout,
  Typography,
  Collapse,
  Card,
  Button,
  Radio,
  Space,
  Spin,
  message,
  Modal,
  Form,
  Input,
  DatePicker,
  InputNumber,
  Breadcrumb,
  Tag,
  Divider
} from 'antd';
import {
  HomeOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  DownOutlined
} from '@ant-design/icons';
import { specialPackageAPI, bookingAPI } from '../utils/api';
import { SpecialPackage, BookingFormData } from '../types';
import dayjs from 'dayjs';
import './TourDetailPage.css';

const { Header, Content, Footer } = Layout;
const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;
const { TextArea } = Input;

const TourDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tour, setTour] = useState<SpecialPackage | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedType, setSelectedType] = useState<'standard' | 'luxury'>('standard');
  const [bookingModalVisible, setBookingModalVisible] = useState<boolean>(false);
  const [form] = Form.useForm<BookingFormData>();

  useEffect(() => {
    if (id) {
      fetchTourDetails();
    }
    // eslint-disable-next-line
  }, [id]);

  const fetchTourDetails = async () => {
    try {
      setLoading(true);
      const response = await specialPackageAPI.getOne(id!);
      setTour(response.data.package);
    } catch (error) {
      console.error('Error fetching tour details:', error);
      message.error('Failed to load tour details');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  // UPDATED: Get current price with discount support
  const getCurrentPrice = (): number => {
    if (!tour) return 0;
    
    // Use discounted prices if discount is available
    if (tour.hasDiscount && tour.discount > 0) {
      return selectedType === 'luxury' 
        ? tour.discountedLuxuryPrice 
        : tour.discountedStandardPrice;
    }
    
    // Otherwise use regular prices
    return selectedType === 'luxury' 
      ? tour.luxuryPrice 
      : tour.standardPrice;
  };

  // UPDATED: Get original price (before discount)
  const getOriginalPrice = (): number => {
    if (!tour) return 0;
    return selectedType === 'luxury' ? tour.luxuryPrice : tour.standardPrice;
  };

  const handleBooking = async (values: BookingFormData) => {
    if (!tour) return;

    try {
      const bookingData = {
        specialPackageId: tour._id,
        packageName: tour.name,
        customerName: values.customerName,
        email: values.email,
        phone: values.phone,
        date: values.date.toISOString(),
        numberOfPeople: values.numberOfPeople,
        packageType: selectedType,
        totalAmount: getCurrentPrice() * values.numberOfPeople,
        specialRequests: values.specialRequests || '',
        bookingType: 'special' as const,
      };

      await bookingAPI.create(bookingData);
      message.success('Booking request submitted successfully! We will contact you soon.');
      setBookingModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Error creating booking:', error);
      message.error('Failed to submit booking request');
    }
  };

  const getMealsBadges = (meals: any) => {
    const badges = [];
    if (meals.breakfast) badges.push(<Tag key="breakfast" color="orange">Breakfast</Tag>);
    if (meals.lunch) badges.push(<Tag key="lunch" color="green">Lunch</Tag>);
    if (meals.dinner) badges.push(<Tag key="dinner" color="blue">Dinner</Tag>);
    return badges;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!tour) {
    return <div>Tour not found</div>;
  }

  return (
    <Layout className="tour-detail-layout">
      {/* Header */}
      <Header className="tour-header">
        <div className="header-content">
          <div className="logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src="/images/PradeepToursLogo.png" alt="Logo" style={{ height: '50px' }} />
            <Text strong className="title-text">Pradeep Tours</Text>
          </div>
          {/* <Button type="primary" size="large" onClick={() => navigate('/')}>
            Back to Home
          </Button> */}
        </div>
      </Header>

      <Content>
        {/* Hero Section */}
        <div
          className="tour-hero"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${tour.image || '/images/default-tour.jpg'})`,
          }}
        >
          <div className="tour-hero-content">
            <Breadcrumb className="tour-breadcrumb">
              <Breadcrumb.Item>
                <HomeOutlined onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />
              </Breadcrumb.Item>
              <Breadcrumb.Item>Tours</Breadcrumb.Item>
              <Breadcrumb.Item>{tour.name}</Breadcrumb.Item>
            </Breadcrumb>
            <Title level={1} className="tour-title">
              {tour.name}
            </Title>
            {tour.hasDiscount && tour.discount > 0 && (
              <div style={{
                display: 'inline-block',
                background: '#ff4d4f',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '4px',
                fontSize: '16px',
                fontWeight: 'bold',
                marginTop: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
              }}>
                🎉 {tour.discount}% OFF - Special Offer!
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="tour-content-wrapper">
          <div className="container">
            <div className="tour-grid">
              {/* Left Column - Tour Details */}
              <div className="tour-main-content">
                {/* Overview Section */}
                <section className="tour-section">
                  <Title level={2}>Explore This Tour</Title>
                  <Paragraph style={{ fontSize: '16px', lineHeight: '1.8' }}>
                    {tour.description}
                  </Paragraph>
                </section>

                {/* Tour Plan Section */}
                <section className="tour-section">
                  <Title level={2}>Tour Plan</Title>
                  <Collapse
                    accordion
                    defaultActiveKey={['1']}
                    expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
                    className="tour-collapse"
                  >
                    {tour.dayPlans.map((day, index) => (
                      <Panel
                        header={
                          <div className="day-panel-header">
                            <Text strong style={{ fontSize: '16px' }}>
                              Day {day.dayNumber} | {day.title}
                            </Text>
                          </div>
                        }
                        key={index + 1}
                      >
                        <div className="day-content">
                          <Paragraph style={{ marginBottom: '16px' }}>
                            {day.description}
                          </Paragraph>
                          
                          {day.activities && (
                            <div style={{ marginBottom: '16px' }}>
                              <Text strong>Activities/Locations:</Text>
                              <Paragraph style={{ marginTop: '8px', marginLeft: '16px' }}>
                                {day.activities}
                              </Paragraph>
                            </div>
                          )}

                          {(day.meals.breakfast || day.meals.lunch || day.meals.dinner) && (
                            <div>
                              <Text strong>Meals: </Text>
                              <Space style={{ marginTop: '8px' }}>
                                {getMealsBadges(day.meals)}
                              </Space>
                              {day.meals.details && (
                                <Paragraph style={{ marginTop: '8px', fontStyle: 'italic', color: '#666' }}>
                                  {day.meals.details}
                                </Paragraph>
                              )}
                            </div>
                          )}
                        </div>
                      </Panel>
                    ))}
                  </Collapse>
                </section>

                {/* Additional Info Section */}
                <section className="tour-section">
                  <Title level={2}>Explore This Tour</Title>
                  <Paragraph style={{ fontSize: '16px', color: '#666' }}>
                    This carefully crafted tour offers an unforgettable experience exploring {tour.destination}. 
                    Each day is designed to provide the perfect balance of adventure, culture, and relaxation.
                  </Paragraph>
                </section>
              </div>

              {/* Right Sidebar - Booking Details */}
              <div className="tour-sidebar">
                <Card className="booking-card">
                  {/* Include/Excluded */}
                  <div className="booking-section">
                    <div className="section-icon-header">
                      <CheckCircleOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                      <Title level={5} style={{ margin: 0 }}>Include/Excluded</Title>
                    </div>
                    <div className="include-list">
                      {tour.includes.map((item, index) => (
                        <div key={index} className="include-item">
                          <CheckCircleOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
                          <Text>{item}</Text>
                        </div>
                      ))}
                      {tour.excludes.map((item, index) => (
                        <div key={`exclude-${index}`} className="include-item">
                          <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: '8px' }} />
                          <Text type="secondary">{item}</Text>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Divider />

                  <div className="booking-section">
                    <div className="section-icon-header">
                      <DollarOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                      <Title level={5} style={{ margin: 0 }}>{tour.showPrice ? 'From' : 'Pricing'}</Title>
                    </div>
                    
                    {tour.showPrice ? (
                            <>
                              {/* Discount Badge */}
                              {tour.hasDiscount && tour.discount > 0 && (
                                <div style={{
                                  background: '#ff4d4f',
                                  color: 'white',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  display: 'inline-block',
                                  marginBottom: '8px',
                                  fontSize: '12px',
                                  fontWeight: 'bold'
                                }}>
                                  {tour.discount}% OFF
                                </div>
                              )}
                              
                              {/* Discounted/Regular Price */}
                              <Title level={2} style={{ margin: '8px 0', color: '#1890ff' }}>
                                ${getCurrentPrice().toFixed(2)}
                              </Title>
                              
                              {/* Original Price (strikethrough if discounted) */}
                              {tour.hasDiscount && tour.discount > 0 && (
                                <div style={{ 
                                  textDecoration: 'line-through', 
                                  color: '#999', 
                                  fontSize: '16px',
                                  marginBottom: '4px'
                                }}>
                                  ${getOriginalPrice().toFixed(2)}
                                </div>
                              )}
                              
                              <Text type="secondary">per person</Text>
                              
                              {/* Savings Display */}
                              {tour.hasDiscount && tour.discount > 0 && (
                                <div style={{
                                  background: '#f6ffed',
                                  border: '1px solid #b7eb8f',
                                  borderRadius: '4px',
                                  padding: '8px',
                                  marginTop: '12px'
                                }}>
                                  <Text style={{ color: '#52c41a', fontSize: '12px', fontWeight: 'bold' }}>
                                    💰 Save ${(getOriginalPrice() - getCurrentPrice()).toFixed(2)} per person!
                                  </Text>
                                </div>
                              )}
                            </>
                          ) : (
                            // PRICE IS HIDDEN - Show contact message
                            <>
                              <Title level={3} style={{ margin: '16px 0', color: '#1890ff' }}>
                                Contact for Pricing
                              </Title>
                              <div style={{
                                background: '#f0f9ff',
                                border: '1px solid #91d5ff',
                                borderRadius: '4px',
                                padding: '12px',
                                marginTop: '12px'
                              }}>
                                <Text style={{ fontSize: '14px' }}>
                                  📞 Please contact us for customized pricing based on your group size and preferences.
                                </Text>
                              </div>
                            </>
                          )}
                  </div>

                  <Divider />

                  {/* Duration */}
                  <div className="booking-section">
                    <div className="section-icon-header">
                      <ClockCircleOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                      <Title level={5} style={{ margin: 0 }}>Duration</Title>
                    </div>
                    <Title level={3} style={{ margin: '8px 0' }}>
                      {tour.days} Days / {tour.nights} Nights
                    </Title>
                  </div>

                  <Divider />

                  {/* Package Type Selection */}
                  <div className="booking-section">
                    <Title level={5} style={{ marginBottom: '12px' }}>Select Package Type:</Title>
                    <Radio.Group
                      value={selectedType}
                      onChange={(e) => setSelectedType(e.target.value)}
                      style={{ width: '100%' }}
                    >
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Card
                          className={`package-type-card ${selectedType === 'luxury' ? 'selected' : ''}`}
                          size="small"
                          onClick={() => setSelectedType('luxury')}
                        >
                          <Radio value="luxury">
                            <div>
                              <Text strong>Luxury</Text>
                              <div>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  Premium accommodations
                                </Text>
                              </div>
                            </div>
                          </Radio>
                        </Card>
                        <Card
                          className={`package-type-card ${selectedType === 'standard' ? 'selected' : ''}`}
                          size="small"
                          onClick={() => setSelectedType('standard')}
                        >
                          <Radio value="standard">
                            <div>
                              <Text strong>Standard</Text>
                              <div>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  Comfortable accommodations
                                </Text>
                              </div>
                            </div>
                          </Radio>
                        </Card>
                      </Space>
                    </Radio.Group>
                  </div>

                  {/* Book Button */}
                  <Button
                    type="primary"
                    size="large"
                    block
                    icon={<CalendarOutlined />}
                    onClick={() => setBookingModalVisible(true)}
                    style={{ marginTop: '24px' }}
                  >
                    Request a Reservation
                  </Button>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </Content>

      {/* Footer */}
      <Footer style={{ textAlign: 'center', background: '#001529', color: 'white' }}>
        © 2025 Pradeep Tours Sri Lanka. All rights reserved. | Designed by VeloxEngine
      </Footer>

      {/* Booking Modal */}
      <Modal
        title={`Book ${tour.name}`}
        open={bookingModalVisible}
        onCancel={() => {
          setBookingModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleBooking}>
          <Form.Item
            name="customerName"
            label="Full Name"
            rules={[{ required: true, message: 'Please enter your name' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="Your name" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="your@email.com" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone Number"
            rules={[{ required: true, message: 'Please enter your phone number' }]}
          >
            <Input prefix={<PhoneOutlined />} placeholder="+94771234567" />
          </Form.Item>

          <Form.Item
            name="date"
            label="Trip Start Date"
            rules={[{ required: true, message: 'Please select a date' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              disabledDate={(current) => current && current < dayjs().startOf('day')}
              format="MMMM DD, YYYY"
            />
          </Form.Item>

          <Form.Item
            name="numberOfPeople"
            label="Number of People"
            rules={[{ required: true, message: 'Please enter number of people' }]}
            initialValue={1}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <div style={{
            padding: '12px',
            background: '#f0f9ff',
            borderRadius: '4px',
            marginBottom: '16px'
          }}>
            <Text strong>Selected Package: </Text>
            <Text>{selectedType === 'luxury' ? 'Luxury' : 'Standard'}</Text>
            <br />
            <Text strong>Price per person: </Text>
            {tour.hasDiscount && tour.discount > 0 ? (
              <>
                <Text delete style={{ color: '#999' }}>${getOriginalPrice()}</Text>
                {' '}
                <Text style={{ color: '#f5222d', fontWeight: 'bold' }}>${getCurrentPrice()}</Text>
                <Tag color="red" style={{ marginLeft: '8px' }}>{tour.discount}% OFF</Tag>
              </>
            ) : (
              <Text>${getCurrentPrice()}</Text>
            )}
          </div>

          <Form.Item name="specialRequests" label="Special Requests (Optional)">
            <TextArea rows={3} placeholder="Any special requirements or preferences" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block size="large">
              Submit Booking Request
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default TourDetailPage;
